import { useState } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  Box,
  ButtonPrimary,
  ButtonSecondary,
  Error,
  HStack,
  VStack,
  getCompanyName,
} from "../../components/utils";
import { generateGrantUploadTemplateWithPrefill } from "../../api/Esop";
import { Input, Label } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import { useEsopPlans } from "../../queries";
import { PlanState, RoundingStrategy, EsopPlan } from "../../types/EsopPlan";
import { downloadBlobObject } from "../../utils/DownloadFile";
import { useGetAllMatrices } from "../../queries/allocationMatrix";
import { useRegionalGrantRules } from "../../queries/onBoarding";
import { formatDate } from "../../utils/date";
import { resolveRegionalGrantRule } from "../../utils/regionalGrantRules";
import { useCompanyStore } from "../../store";
import { SwitchButton } from "../../components/shared/SwitchButton";

enum VestingDateType {
  GRANT_DATE = "GRANT_DATE",
  EMPLOYEE_JOINING_DATE = "EMPLOYEE_JOINING_DATE",
  CUSTOM_DATE = "CUSTOM_DATE",
}

const VESTING_SCHEDULE_OPTIONS = [
  { value: VestingDateType.GRANT_DATE, label: "Vesting From date of Grant" },
  {
    value: VestingDateType.EMPLOYEE_JOINING_DATE,
    label: "Vesting From date of Joining",
  },
  { value: VestingDateType.CUSTOM_DATE, label: "Custom Vesting" },
];

interface IssueNewGrantsDialogProps {
  onClose: () => void;
}

interface FormValues {
  matrixId: string;
  planId: string;
  dateOfGrant: string;
  grantPrice: string;
  vestingDateType: string;
  onlyEmployedEmployees: boolean;
}

function IssueNewGrantsDialog({ onClose }: IssueNewGrantsDialogProps) {
  const { data: _plans } = useEsopPlans();
  const { data: matrices } = useGetAllMatrices();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<EsopPlan | null>(null);
  const companyName = getCompanyName();
  const { companyData } = useCompanyStore();
  const { data: regionalRuleCatalog } = useRegionalGrantRules();
  const regionalRule = resolveRegionalGrantRule(
    regionalRuleCatalog?.rules,
    companyData?.companyMetaData?.registeredCountry
  );
  const isStrikePriceOptional =
    selectedPlan?.planType === "ESOP" &&
    regionalRule.strikePriceOptional !== undefined;

  const plans = _plans || [];
  const allMatrices =
    matrices?.filter((matrix) => matrix.status === "ACTIVE") || [];

  const activePlans = plans.filter((p) => p.esopPlanState === PlanState.ACTIVE);

  const initialValues: FormValues = {
    matrixId: "",
    planId: "",
    dateOfGrant: formatDate(new Date().toUTCString()),
    grantPrice: "",
    vestingDateType: VestingDateType.GRANT_DATE,
    onlyEmployedEmployees: true,
  };

  const validationSchema = Yup.object({
    matrixId: Yup.string().required("Allocation matrix is required"),
    planId: Yup.string().required("Plan is required"),
    dateOfGrant: Yup.string().required("Date of grant is required"),
    grantPrice: Yup.string()
      .test(
        "grant-price-required",
        "Grant price is required",
        (value) => isStrikePriceOptional || Boolean(value)
      )
      .test("isNumber", "Grant price must be a valid number", (value) => {
        if (!value) return isStrikePriceOptional;
        return !Number.isNaN(Number(value)) && Number(value) >= 0;
      })
      .test(
        "regional-strike-price-required",
        `Grant price is required for ${regionalRule.country}`,
        (value) => {
          if (value === undefined || value === null) return true;
          if (selectedPlan?.planType === "RSU") return Number(value) >= 0.0001;
          if (selectedPlan?.planType !== "ESOP") return Number(value) >= 0;
          return isStrikePriceOptional || Number(value) > 0;
        }
      )
      .test(
        "regional-grant-price-minimum",
        `Grant price must be at least ${regionalRule.minStrikePrice}`,
        (value) =>
          value === undefined ||
          value === null ||
          selectedPlan?.planType !== "ESOP" ||
          isStrikePriceOptional ||
          regionalRule.minStrikePrice === undefined ||
          Number(value) >= regionalRule.minStrikePrice
      ),
    vestingDateType: Yup.string().required("Vesting type is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const response = await generateGrantUploadTemplateWithPrefill({
          matrixId: values.matrixId,
          planId: values.planId,
          dateOfGrant: values.dateOfGrant,
          grantPrice: values.grantPrice,
          vestingDateType: values.vestingDateType,
          onlyEmployedEmployees: values.onlyEmployedEmployees,
        });

        const base64File = response.data?.base64File;
        if (base64File) {
          try {
            const base64 = base64File.includes(",")
              ? base64File.split(",")[1]
              : base64File;
            const binary = atob(base64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
              bytes[i] = binary.charCodeAt(i);
            }
            const blob = new Blob([bytes], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const fileName = `Grant_Upload_Template_${companyName}.xlsx`;
            downloadBlobObject(blob, fileName);

            toast("Excel template downloaded successfully", {
              type: "success",
              autoClose: 2000,
            });
            onClose();
          } catch (downloadErr: any) {
            toast(`Download error: ${downloadErr.message}`, {
              type: "error",
              autoClose: 2000,
            });
          }
        } else {
          toast("Failed to generate template", {
            type: "error",
            autoClose: 2000,
          });
        }
      } catch (err: any) {
        const errorMsg =
          err?.response?.data?.message ||
          err?.response?.data?.errorMessage ||
          "Failed to generate grant upload template";
        toast(errorMsg, { type: "error", autoClose: 2000 });
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <VStack className="w-full max-w-2xl bg-white rounded-lg shadow-lg min-w-[600px]">
      <div className="p-8 text-lg font-medium border-b flex items-center justify-between">
        <h6>Issue New Grants</h6>
        <span
          className="cursor-pointer text-gray-light hover:text-primaryText"
          onClick={() => onClose()}
        >
          ✕
        </span>
      </div>

      <VStack className="p-8 gap-6 w-full">
        <VStack className="gap-2">
          <Label>
            Plan
            <span className="text-red-500 ml-0.5">*</span>
          </Label>
          <Select
            value={formik.values.planId}
            onChange={(e) => {
              formik.setFieldValue("planId", e.target.value);
              const plan = activePlans.find(
                (p) => p.esopPlanId === e.target.value
              );
              setSelectedPlan(plan || null);
            }}
            onBlur={formik.handleBlur}
            options={activePlans}
            valueGetter={(plan: any) => plan.esopPlanId}
            textGetter={(plan: any) => plan.planName}
            placeholder="Select a plan"
          />
          {formik.touched.planId && formik.errors.planId && (
            <Error text={formik.errors.planId} />
          )}
        </VStack>

        <VStack className="gap-2">
          <Label>
            Allocation Matrix
            <span className="text-red-500 ml-0.5">*</span>
          </Label>
          <Select
            value={formik.values.matrixId}
            onChange={(e) => formik.setFieldValue("matrixId", e.target.value)}
            onBlur={formik.handleBlur}
            options={allMatrices}
            valueGetter={(matrix: any) => matrix.id}
            textGetter={(matrix: any) => matrix.name}
            placeholder="Select an allocation matrix"
          />
          {formik.touched.matrixId && formik.errors.matrixId && (
            <Error text={formik.errors.matrixId} />
          )}
        </VStack>

        <VStack className="gap-2">
          <Label>
            Date of Grant
            <span className="text-red-500 ml-0.5">*</span>
          </Label>
          <Input
            type="date"
            value={formik.values.dateOfGrant}
            onChange={(e) =>
              formik.setFieldValue("dateOfGrant", e.target.value)
            }
            onBlur={formik.handleBlur}
          />
          {formik.touched.dateOfGrant && formik.errors.dateOfGrant && (
            <Error text={formik.errors.dateOfGrant} />
          )}
        </VStack>

        <VStack className="gap-2">
          <Label>
            Grant Price{" "}
            {companyData?.currency &&
              `(${companyData.currency.split(" - ")[1]})`}
            <span className="text-red-500 ml-0.5">*</span>
          </Label>
          <Input
            type="number"
            step="0.01"
            placeholder="Enter grant price"
            value={formik.values.grantPrice}
            onChange={(e) => formik.setFieldValue("grantPrice", e.target.value)}
            onBlur={formik.handleBlur}
          />
          {formik.touched.grantPrice && formik.errors.grantPrice && (
            <Error text={formik.errors.grantPrice} />
          )}
        </VStack>

        <VStack className="gap-2">
          <Label>
            Vesting Date Type
            <span className="text-red-500 ml-0.5">*</span>
          </Label>
          <Select
            value={formik.values.vestingDateType || VestingDateType.GRANT_DATE}
            onChange={(e) =>
              formik.setFieldValue("vestingDateType", e.target.value)
            }
            onBlur={formik.handleBlur}
            options={VESTING_SCHEDULE_OPTIONS}
            valueGetter={(option: any) => option.value}
            textGetter={(option: any) => option.label}
            placeholder="Select a Vesting Date Type"
          />
          {formik.touched.vestingDateType && formik.errors.vestingDateType && (
            <Error text={formik.errors.vestingDateType} />
          )}
        </VStack>

        {selectedPlan?.roundingSettings && (
          <VStack className="gap-3 border-t pt-6">
            <VStack className="gap-2">
              <Label className="font-medium text-sm">Rounding Settings</Label>
              <Box className="bg-blue-50 border border-blue-200 rounded p-3">
                <VStack className="gap-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Strategy:</span>
                    <span className="font-medium">
                      {selectedPlan.roundingSettings.strategy ===
                      RoundingStrategy.ROUND_UP
                        ? "Round Up"
                        : selectedPlan.roundingSettings.strategy ===
                          RoundingStrategy.ROUND_DOWN
                        ? "Round Down"
                        : "Round Off"}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Multiple:</span>
                    <span className="font-medium">
                      {selectedPlan.roundingSettings.multiple}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 mt-2 leading-relaxed">
                    When grants are issued based on value (allocation matrix),
                    the calculated options will be rounded to the nearest
                    multiple of{" "}
                    <span className="font-medium">
                      {selectedPlan.roundingSettings.multiple}
                    </span>{" "}
                    using{" "}
                    <span className="font-medium">
                      {selectedPlan.roundingSettings.strategy ===
                      RoundingStrategy.ROUND_UP
                        ? "round up"
                        : selectedPlan.roundingSettings.strategy ===
                          RoundingStrategy.ROUND_DOWN
                        ? "round down"
                        : "standard rounding"}
                    </span>{" "}
                    logic.
                  </div>
                </VStack>
              </Box>
            </VStack>
          </VStack>
        )}

        <VStack className="gap-3 border-t pt-6">
          <HStack className="gap-3 items-stretch">
            <div className="flex items-center">
              <SwitchButton
                value={formik.values.onlyEmployedEmployees}
                label=""
                onClick={() =>
                  formik.setFieldValue(
                    "onlyEmployedEmployees",
                    !formik.values.onlyEmployedEmployees
                  )
                }
              />
            </div>
            <VStack className="flex-1">
              <Label className="font-medium text-sm">
                Include Currently Employed Employees Only
              </Label>
              <span className="text-xs text-gray-500 leading-relaxed">
                {"Template will list only active employees"}
              </span>
            </VStack>
          </HStack>
        </VStack>
      </VStack>

      <HStack className="justify-between px-8 py-6 border-t w-full">
        <ButtonSecondary onClick={() => onClose()}>Cancel</ButtonSecondary>
        <ButtonPrimary
          onClick={() => formik.handleSubmit()}
          loading={isLoading}
          disabled={isLoading}
        >
          Download Template
        </ButtonPrimary>
      </HStack>
    </VStack>
  );
}

export default IssueNewGrantsDialog;
